// @flow

import React, { useMemo } from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import { Section, Wrapper } from '@riseart/layout';
import { Button } from '@riseart/common';
import { GridEvenColumns } from '@riseart/grid';
import { ProfileListCard } from '@riseart/artist';
import { useQuery } from 'shared_services/apollo/useQuery';
import { handlePrintClick } from 'shared_services/riseart/utils/Utils';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import READ_ARTIST_CV from 'shared_data/queries/artist/readCv.graphql';

import { artistCvPrintCls } from 'shared_components/pages/artist/Cv.less';

type Props = {
  artistId: number,
  loading: boolean,
  children: Node,
};

/**
 * ArtistCv
 */
export function ArtistCv({ artistId, loading, children }: Props): Node {
  const { data: cvData, loading: cvLoading } = useQuery(READ_ARTIST_CV, {
    ssr: false,
    variables: { artistId },
    skip: !artistId,
  });
  const {
    degrees: { items: degrees } = { items: null },
    awards: { items: awards } = { items: null },
    exhibitions: { items: exhibitions } = { items: null },
    collections: { items: collections } = { items: null },
    bibliographies: { items: bibliographies } = { items: null },
  } = cvData || {};
  const exhibitionsByType = useMemo(
    () =>
      exhibitions &&
      exhibitions.reduce((accumulator, { type, year, title, venue }) => {
        accumulator[type] = [
          ...(accumulator[type] || []),
          {
            year,
            text: [title, venue].filter((field) => !!field).join(', '),
          },
        ];

        return accumulator;
      }, {}),
    [exhibitions],
  );

  // Expand button used for the cv list sections
  const expandButtonLabel = (
    <FormattedMessage id="components.artist.expandButtonLabel">
      {(label: string) => label}
    </FormattedMessage>
  );

  return (
    <Section>
      <IsomorphicRipple isActive={cvLoading}>
        <Wrapper sizes={{ sm: 'narrow', md: 'narrow' }}>
          {!loading ? (
            <GridEvenColumns itemMargin={30}>
              {/* Main Information - Should be always visible, because data is loaded server-side or from cache */}
              {children}
              {/* Degrees */}
              {degrees && degrees.length ? (
                <ProfileListCard
                  expandButtonLabel={expandButtonLabel}
                  title={
                    <FormattedMessage id="components.artist.education">
                      {(label: string) => label}
                    </FormattedMessage>
                  }
                  itemRenderer={(item) => (
                    <FormattedMessage id={`countries.${item.countryCode}`}>
                      {(country: string) =>
                        [item.description, item.university, item.city, country]
                          .filter((field) => !!field)
                          .join(', ')
                      }
                    </FormattedMessage>
                  )}
                  leftField="year"
                  items={degrees}
                />
              ) : null}
              {/* Awards */}
              {awards && awards.length ? (
                <ProfileListCard
                  expandButtonLabel={expandButtonLabel}
                  title={
                    <FormattedMessage id="components.artist.awards">
                      {(label: string) => label}
                    </FormattedMessage>
                  }
                  leftField="year"
                  itemRenderer={(item) => (
                    <FormattedMessage id={`countries.${item.countryCode}`}>
                      {(country: string) =>
                        [item.name, item.body, item.city, country]
                          .filter((field) => !!field)
                          .join(', ')
                      }
                    </FormattedMessage>
                  )}
                  items={awards}
                />
              ) : null}
              {/* Solo Exhibitions */}
              {exhibitionsByType && exhibitionsByType.solo && exhibitionsByType.solo.length ? (
                <ProfileListCard
                  expandButtonLabel={expandButtonLabel}
                  title={
                    <FormattedMessage id="components.artist.soloExhibitions">
                      {(label: string) => label}
                    </FormattedMessage>
                  }
                  leftField="year"
                  itemRenderer={({ text }) => text}
                  items={exhibitionsByType.solo}
                />
              ) : null}
              {/* Group Exhibitions */}
              {exhibitionsByType && exhibitionsByType.group && exhibitionsByType.group.length ? (
                <ProfileListCard
                  expandButtonLabel={expandButtonLabel}
                  title={
                    <FormattedMessage id="components.artist.groupExhibitions">
                      {(label: string) => label}
                    </FormattedMessage>
                  }
                  leftField="year"
                  itemRenderer={({ text }) => text}
                  items={exhibitionsByType.group}
                />
              ) : null}
              {/* Collections */}
              {collections && collections.length ? (
                <ProfileListCard
                  expandButtonLabel={expandButtonLabel}
                  title={
                    <FormattedMessage id="components.artist.collections">
                      {(label: string) => label}
                    </FormattedMessage>
                  }
                  itemRenderer={(item) => (
                    <FormattedMessage id={`countries.${item.countryCode}`}>
                      {(country: string) =>
                        [item.collection, item.city, item.countryCode && country]
                          .filter((field) => !!field)
                          .join(', ')
                      }
                    </FormattedMessage>
                  )}
                  items={collections}
                />
              ) : null}
              {/* Bibliographies */}
              {bibliographies && bibliographies.length ? (
                <ProfileListCard
                  expandButtonLabel={expandButtonLabel}
                  title={
                    <FormattedMessage id="components.artist.bibliography">
                      {(label: string) => label}
                    </FormattedMessage>
                  }
                  itemRenderer={(item) => {
                    // Flag set to true only after first item to show has any value,
                    // so that the next items should include their separators when appending
                    let includeSeparator = false;

                    return [
                      { separator: null, value: item.author },
                      {
                        separator: ', ',
                        value: item.title ? `"${item.title}"` : null,
                      },
                      {
                        separator: ', ',
                        value: item.publication ? <em>{item.publication}</em> : null,
                      },
                      { separator: ' ', value: item.publisher },
                      { separator: ' ', value: item.year },
                      { separator: ': ', value: item.pages },
                    ].map(({ separator, value }, key) => {
                      // exclude falsey values
                      if (!value) {
                        return null;
                      }

                      const item = (
                        <React.Fragment key={key}>
                          {includeSeparator && separator ? separator : null}
                          {value}
                        </React.Fragment>
                      );

                      // Raise the flag when any first item has valid value and will be visible
                      if (!includeSeparator) {
                        includeSeparator = true;
                      }

                      return item;
                    });
                  }}
                  items={bibliographies}
                />
              ) : null}
            </GridEvenColumns>
          ) : null}
          {!loading && !cvLoading ? (
            <div className={artistCvPrintCls}>
              <Button type="secondary" size="medium" onClick={handlePrintClick}>
                <FormattedMessage id="components.artist.printCV">
                  {(text: string) => text}
                </FormattedMessage>
              </Button>
            </div>
          ) : null}
        </Wrapper>
      </IsomorphicRipple>
    </Section>
  );
}
